import { useState } from "react";
import { useLocation } from "wouter";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useCreateMyUpdate,
  getGetMyUpdatesQueryKey,
} from "@workspace/api-client-react";
import { useUpload } from "@workspace/object-storage-web";
import { useQueryClient } from "@tanstack/react-query";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@workspace/ui-web/components/ui/button";
import { Input } from "@workspace/ui-web/components/ui/input";
import { Textarea } from "@workspace/ui-web/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui-web/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui-web/components/ui/form";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui-web/components/ui/card";
import { useToast } from "@workspace/ui-web/hooks/use-toast";
import { Loader2, ArrowLeft, Plus, Trash2, ImagePlus, X } from "lucide-react";
import { Link } from "wouter";
import { getImageUrl } from "@/lib/utils";

const updateSchema = z.object({
  note: z.string().min(1, "Update note is required"),
  weightLbs: z.coerce
    .number()
    .optional()
    .or(z.literal("").transform(() => undefined)),
  milestones: z
    .array(
      z.object({
        category: z.enum(["training", "health", "other"]),
        description: z.string().min(1, "Description is required"),
      }),
    )
    .optional(),
});

type UpdateFormValues = z.infer<typeof updateSchema>;

export default function PortalUpdateNew() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createUpdate = useCreateMyUpdate();

  const [photos, setPhotos] = useState<string[]>([]);
  const { uploadFile, isUploading } = useUpload({
    basePath: `${import.meta.env.BASE_URL}api/storage`,
    onError: () =>
      toast({
        title: "Upload failed",
        description: "That photo could not be uploaded. Please try again.",
        variant: "destructive",
      }),
  });

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    for (const file of files) {
      const result = await uploadFile(file);
      if (result) {
        setPhotos((prev) => [...prev, result.objectPath]);
      }
    }
  };

  const removePhoto = (path: string) => {
    setPhotos((prev) => prev.filter((p) => p !== path));
  };

  const form = useForm<UpdateFormValues>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      note: "",
      weightLbs: undefined,
      milestones: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "milestones",
  });

  const onSubmit = (data: UpdateFormValues) => {
    createUpdate.mutate(
      {
        data: {
          note: data.note,
          weightLbs: data.weightLbs,
          milestones: data.milestones,
          photos: photos,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetMyUpdatesQueryKey(),
          });
          toast({
            title: "Update saved",
            description: "Your progress has been recorded.",
          });
          setLocation("/portal");
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to save update.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <Navbar />
      <main className="flex-1 container px-4 py-8 mx-auto max-w-3xl">
        <div className="mb-6">
          <Link
            href="/portal"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Portal
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Log a New Update</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8"
              >
                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>General Note *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="How has the dog been doing this week? Any behavioral observations?"
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="weightLbs"
                    render={({ field }) => (
                      <FormItem className="max-w-[200px]">
                        <FormLabel>Weight (lbs)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="e.g. 45.5"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4 pt-6 border-t">
                  <div>
                    <h3 className="text-lg font-medium">Photos</h3>
                    <p className="text-sm text-muted-foreground">
                      Share recent pictures of the dog's progress.
                    </p>
                  </div>

                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                      {photos.map((path) => (
                        <div
                          key={path}
                          className="relative aspect-square overflow-hidden rounded-lg border bg-muted"
                        >
                          <img
                            src={getImageUrl(path)}
                            alt="Update"
                            className="h-full w-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(path)}
                            className="absolute right-1 top-1 rounded-full bg-background/80 p-1 text-foreground hover:bg-background"
                            aria-label="Remove photo"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <label className="flex w-fit cursor-pointer items-center gap-2 rounded-md border border-dashed px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/50">
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    {isUploading ? "Uploading..." : "Add photos"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handlePhotoSelect}
                      disabled={isUploading}
                    />
                  </label>
                </div>

                <div className="space-y-4 pt-6 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium">
                        Milestones & Health
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Log specific training achievements or health events.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        append({ category: "training", description: "" })
                      }
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" /> Add Item
                    </Button>
                  </div>

                  {fields.length === 0 ? (
                    <div className="text-center py-6 bg-muted/30 border border-dashed rounded-lg text-muted-foreground text-sm">
                      No specific milestones added.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {fields.map((field, index) => (
                        <div key={field.id} className="flex gap-3 items-start">
                          <FormField
                            control={form.control}
                            name={`milestones.${index}.category`}
                            render={({ field: selectField }) => (
                              <FormItem className="w-[140px] shrink-0">
                                <Select
                                  onValueChange={selectField.onChange}
                                  defaultValue={selectField.value}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Category" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="training">
                                      Training
                                    </SelectItem>
                                    <SelectItem value="health">
                                      Health
                                    </SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`milestones.${index}.description`}
                            render={({ field: inputField }) => (
                              <FormItem className="flex-1">
                                <FormControl>
                                  <Input
                                    placeholder="e.g. Mastered 'leave it' with high value treats"
                                    {...inputField}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => remove(index)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-4 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation("/portal")}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createUpdate.isPending || isUploading}
                  >
                    {createUpdate.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Submit Update
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
